import { useParams } from 'react-router-dom';
import Seo, { SITE_NAME, SITE_ORIGIN } from '@/components/feature/Seo';
import Breadcrumb from '@/components/base/Breadcrumb';
import CategorySidebar from '@/pages/category/components/CategorySidebar';
import CategoryContent from '@/pages/category/components/CategoryContent';
import { useCategoryArticles } from '@/pages/category/useCategoryArticles';

export default function CategoryPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const { category, articles, totalCount } = useCategoryArticles(slug);

  if (!category) {
    return (
      <div className="mx-auto max-w-[1280px] px-4 py-24 text-center md:px-6">
        <i className="ri-ghost-line text-5xl text-foreground-300" />
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">没有找到这个栏目</h1>
        <p className="mt-2 text-sm text-foreground-600">它可能已经调整或更名，去看看其他栏目吧。</p>
      </div>
    );
  }

  const path = `/category/${category.slug}`;

  return (
    <div className="w-full">
      <Seo
        title={`${category.name} - 最新爆料与热门事件合集`}
        description={`${category.name}栏目聚合18爆料网关于${category.name}的最新内容。${category.description}每天更新，持续追更热门事件与真实投稿。`}
        keywords={`${category.name},18爆料网${category.name},${category.name}最新,18爆料网今日爆料,热门爆料`}
        path={path}
        ogType="website"
        schema={[
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${category.name} - ${SITE_NAME}`,
            description: category.description,
            url: `${SITE_ORIGIN}${path}`,
            isPartOf: { '@type': 'WebSite', name: SITE_NAME, url: SITE_ORIGIN },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: SITE_ORIGIN },
              { '@type': 'ListItem', position: 2, name: category.name, item: `${SITE_ORIGIN}${path}` },
            ],
          },
          {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            itemListElement: articles.slice(0, 10).map((item, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              name: item.title,
              url: `${SITE_ORIGIN}/article/${item.id}`,
            })),
          },
        ]}
      />

      <section className="w-full bg-foreground-950 py-10 md:py-14">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <div className="[&_a]:text-background-300 [&_span]:text-background-100">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: category.name }]} />
          </div>
          <div className="mt-5 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-500 text-xl text-background-50">
                  <i className={category.icon} />
                </span>
                <h1 className="font-heading text-3xl font-bold text-background-50 md:text-4xl">{category.name}</h1>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-background-300 md:text-base">{category.intro}</p>
            </div>
            <div className="flex items-center gap-6">
              <div>
                <p className="font-heading text-2xl font-bold text-background-50">{totalCount}</p>
                <p className="text-xs text-background-400">篇爆料</p>
              </div>
              <div>
                <p className="font-heading text-2xl font-bold text-accent-400">每日</p>
                <p className="text-xs text-background-400">持续更新</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-4">
          <div className="lg:col-span-3">
            <CategoryContent articles={articles} />
          </div>
          <CategorySidebar currentSlug={category.slug} />
        </div>
      </section>
    </div>
  );
}